import { debug } from '../lib/debug';
import { fetchDispatchVehicleTypes } from '../api/dispatchService';

const normalizeKey = (value: any): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed ? trimmed.toUpperCase() : '';
};

export const DEFAULT_CAR_TYPE_COLOR = '#2563eb' as const;
export const DEFAULT_CAR_TYPE_TEXT_COLOR = '#ffffff' as const;
export const GATCHA_CAR_TYPE_CODE = 'GATCHA' as const;
export const GATCHA_CAR_TYPE_COLOR = '#8270b1' as const;
export const GATCHA_CAR_TYPE_TEXT_COLOR = '#ffffff' as const;

export const CAR_TYPES: any = {
  list: [],
  byName: new Map(),
  byId: new Map(),
  ready: false,
  lastFetchedAt: null,
};

let loadPromise: any = null;

const extractColorCode = (entry: any): string => {
  if (!entry || typeof entry !== 'object') return '';
  const v2Color = typeof entry.bg_color === 'string' ? entry.bg_color.trim() : '';
  if (v2Color) return v2Color;
  const raw = typeof entry.color1 === 'string' ? entry.color1.trim() : '';
  if (raw) return raw;
  const fallback = typeof entry.color === 'string' ? entry.color.trim() : '';
  return fallback || '';
};

const extractTextColorCode = (entry: any): string => {
  if (!entry || typeof entry !== 'object') return '';
  const val = typeof entry.text_color === 'string' ? entry.text_color.trim() : '';
  return val || '';
};

const assignCarTypes = (types: any) => {
  const entries = Array.isArray(types) ? types : [];
  CAR_TYPES.list = entries;
  CAR_TYPES.byName.clear();
  CAR_TYPES.byId.clear();

  entries.forEach((entry: any) => {
    if (!entry || typeof entry !== 'object') return;
    const v2Name = normalizeKey(entry.vehicle_type_name);
    const v2Code = normalizeKey(entry.vehicle_type_code);
    const nameKey = normalizeKey(entry.name);
    const codeKey = normalizeKey(entry.code);
    const idKey = normalizeKey(entry.id);

    if (v2Name) CAR_TYPES.byName.set(v2Name, entry);
    if (nameKey && !CAR_TYPES.byName.has(nameKey)) {
      CAR_TYPES.byName.set(nameKey, entry);
    }

    if (v2Code && !CAR_TYPES.byName.has(v2Code)) {
      CAR_TYPES.byName.set(v2Code, entry);
    }
    if (codeKey && !CAR_TYPES.byName.has(codeKey)) {
      CAR_TYPES.byName.set(codeKey, entry);
    }
    if (idKey) CAR_TYPES.byId.set(idKey, entry);
  });
};

export const ensureCarTypesLoaded = async (options: any = {}) => {
  const { force = false } = options ?? {};

  if (!force && CAR_TYPES.ready && CAR_TYPES.list.length > 0) {
    return CAR_TYPES;
  }

  if (loadPromise) {
    try {
      await loadPromise;
    } catch (_) {
      /* ignore previous failure */
    }
    if (!force && CAR_TYPES.ready && CAR_TYPES.list.length > 0) {
      return CAR_TYPES;
    }
  }

  CAR_TYPES.ready = false;
  loadPromise = fetchDispatchVehicleTypes()
    .then((entries: any) => {
      assignCarTypes(entries);
      CAR_TYPES.ready = true;
      CAR_TYPES.lastFetchedAt = Date.now();
      debug.log('carTypes', 'loaded car types', { count: entries.length });
      return CAR_TYPES;
    })
    .catch((error: any) => {
      debug.error('carTypes', 'failed to load car types', error);
      assignCarTypes([]);
      CAR_TYPES.ready = false;
      throw error;
    })
    .finally(() => {
      loadPromise = null;
    });

  return loadPromise;
};

export const getCarTypeByName = (name: any) => {
  const key = normalizeKey(name);
  if (!key) return null;
  return CAR_TYPES.byName.get(key) ?? null;
};

export const getCarTypeColor = (name: any, fallbackColor: string = DEFAULT_CAR_TYPE_COLOR) => {
  const entry = getCarTypeByName(name);
  if (!entry) return fallbackColor;
  const color = extractColorCode(entry);
  return color || fallbackColor;
};

export const getCarTypeById = (id: any) => {
  const key = normalizeKey(id);
  if (!key) return null;
  return CAR_TYPES.byId.get(key) ?? null;
};

export const getCarTypeColorById = (id: any, fallbackColor: string = DEFAULT_CAR_TYPE_COLOR) => {
  const entry = getCarTypeById(id);
  if (!entry) return fallbackColor;
  const color = extractColorCode(entry);
  return color || fallbackColor;
};

export const resolveCarTypeColor = (identifier: any, fallbackColor: string = DEFAULT_CAR_TYPE_COLOR) => {
  const key = normalizeKey(identifier);
  if (!key) return fallbackColor;
  if (key === GATCHA_CAR_TYPE_CODE) {
    return GATCHA_CAR_TYPE_COLOR;
  }

  const byNameEntry = CAR_TYPES.byName.get(key);
  if (byNameEntry) {
    const color = extractColorCode(byNameEntry);
    if (color) return color;
  }

  const byIdEntry = CAR_TYPES.byId.get(key);
  if (byIdEntry) {
    const color = extractColorCode(byIdEntry);
    if (color) return color;
  }

  return fallbackColor;
};

export const getCarTypeTextColor = (name: any, fallbackColor: string = DEFAULT_CAR_TYPE_TEXT_COLOR) => {
  const entry = getCarTypeByName(name);
  if (!entry) return fallbackColor;
  const color = extractTextColorCode(entry);
  return color || fallbackColor;
};

export const getCarTypeTextColorById = (id: any, fallbackColor: string = DEFAULT_CAR_TYPE_TEXT_COLOR) => {
  const entry = getCarTypeById(id);
  if (!entry) return fallbackColor;
  const color = extractTextColorCode(entry);
  return color || fallbackColor;
};

export const resolveCarTypeTextColor = (identifier: any, fallbackColor: string = DEFAULT_CAR_TYPE_TEXT_COLOR) => {
  const key = normalizeKey(identifier);
  if (!key) return fallbackColor;
  if (key === GATCHA_CAR_TYPE_CODE) {
    return GATCHA_CAR_TYPE_TEXT_COLOR;
  }

  const byNameEntry = CAR_TYPES.byName.get(key);
  if (byNameEntry) {
    const color = extractTextColorCode(byNameEntry);
    if (color) return color;
  }

  const byIdEntry = CAR_TYPES.byId.get(key);
  if (byIdEntry) {
    const color = extractTextColorCode(byIdEntry);
    if (color) return color;
  }

  return fallbackColor;
};
